import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

const LEGACY_TOOL_OPTIONS = [
    { value: 'None', label: 'None' },
    { value: 'ClickUp', label: 'ClickUp' },
    { value: 'Notion', label: 'Notion' },
    { value: 'Monday', label: 'Monday' },
    { value: 'Slack', label: 'Slack' },
    { value: 'Google Docs', label: 'Google Docs' },
]

const TasksFilterBar = ({
    search,
    onSearchChange,
    selectedTools = [],
    onToggleTool,
    onClearTools,
    toolOptions,
}) => {
    const [filterOpen, setFilterOpen] = useState(false)
    const [toolSearch, setToolSearch] = useState('')
    const filterRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const optionsList = useMemo(() => {
        if (Array.isArray(toolOptions) && toolOptions.length > 0) {
            return toolOptions.map((opt) =>
                typeof opt === 'string' ? { value: opt, label: opt } : opt
            )
        }
        return LEGACY_TOOL_OPTIONS
    }, [toolOptions])

    const filteredOptions = useMemo(
        () =>
            optionsList.filter((opt) =>
                (opt.label || opt.value || '')
                    .toLowerCase()
                    .includes(toolSearch.toLowerCase())
            ),
        [optionsList, toolSearch]
    )

    const labelForValue = useCallback(
        (value) => {
            const found = optionsList.find((opt) => opt.value === value)
            return found?.label || value
        },
        [optionsList]
    )

    const handleToggleFilter = useCallback(() => setFilterOpen((v) => !v), [])
    const handleSearchChange = useCallback(
        (e) => onSearchChange?.(e.target.value),
        [onSearchChange]
    )
    const handleToolSearchChange = useCallback(
        (e) => setToolSearch(e.target.value),
        []
    )

    const hasActiveFilters = selectedTools.length > 0

    return (
        <div className='mb-4'>
            <div className='flex items-center justify-between gap-4'>
                <div className='relative flex-1'>
                    <SearchIcon
                        style={{ fontSize: 18, position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
                    />
                    <input
                        type='text'
                        value={search}
                        onChange={handleSearchChange}
                        placeholder='Search tasks...'
                        style={{ paddingLeft: 40 }}
                        className='w-full pr-4 py-2 text-[13px] rounded-full border !border-[#e2e8f0] bg-white outline-none focus:!border-[#cbd5e1] placeholder:text-[#9ca3af]'
                    />
                </div>

                <div className='relative flex items-center gap-2' ref={filterRef}>
                    <span className='flex items-center gap-1 text-[13px] text-[#6b7280] whitespace-nowrap'>
                        <FilterListIcon style={{ fontSize: 16 }} />
                        Filter by:
                    </span>
                    <button
                        type='button'
                        onClick={handleToggleFilter}
                        className='flex items-center gap-1 px-3 py-1.5 rounded-md border !border-[#e2e8f0] bg-white text-[13px] text-[#374151] hover:bg-[#f8fafc]'
                    >
                        Tools
                        {hasActiveFilters && (
                            <span className='text-[#6b7280]'>({selectedTools.length})</span>
                        )}
                        <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
                    </button>

                    {filterOpen && (
                        <div className='absolute right-0 top-[calc(100%+8px)] z-20 w-[240px] rounded-lg border !border-[#e2e8f0] bg-white shadow-lg p-2'>
                            <div className='relative mb-2'>
                            
                                <input
                                    type='text'
                                    value={toolSearch}
                                    onChange={handleToolSearchChange}
                                    placeholder='Search tools...'
                                    className='w-full pl-7 pr-3 py-1.5 text-[12px] rounded-md border !border-[#e2e8f0] outline-none placeholder:text-[#9ca3af]'
                                />
                            </div>
                            <div className='flex flex-col'>
                                {filteredOptions.length === 0 && (
                                    <span className='px-2 py-1.5 text-[12px] text-[#9ca3af]'>
                                        No tools available
                                    </span>
                                )}
                                {filteredOptions.map((opt) => (
                                    <label
                                        key={opt.value}
                                        className='flex items-center gap-2 px-2 py-1.5 text-[13px] text-[#374151] hover:bg-[#f8fafc] rounded cursor-pointer'
                                    >
                                        <input
                                            type='checkbox'
                                            checked={selectedTools.includes(opt.value)}
                                            onChange={() => onToggleTool?.(opt.value)}
                                            className='w-3.5 h-3.5 accent-black'
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                            {hasActiveFilters && (
                                <div className='border-t !border-[#e2e8f0] mt-2 pt-1'>
                                    <button
                                        type='button'
                                        onClick={onClearTools}
                                        className='w-full text-center px-2 py-1.5 text-[13px] text-[#374151] hover:bg-[#f8fafc] rounded'
                                    >
                                        Clear all
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {hasActiveFilters && (
                <div className='flex items-center flex-wrap gap-2 mt-3'>
                    <span className='text-[12px] text-[#6b7280]'>Active filters:</span>
                    {selectedTools.map((tool) => (
                        <span
                            key={tool}
                            className='inline-flex items-center gap-1.5 px-2 py-1 text-[12px] rounded-full border !border-[#e2e8f0] bg-[#f1f5f9] text-[#374151]'
                        >
                            <LocalOfferOutlinedIcon style={{ fontSize: 12, color: '#9ca3af' }} />
                            {labelForValue(tool)}
                            <button
                                type='button'
                                onClick={() => onToggleTool?.(tool)}
                                className='inline-flex items-center justify-center w-4 h-4 rounded-full text-[#9ca3af] hover:text-black hover:bg-[#f1f5f9]'
                                aria-label={`Remove ${labelForValue(tool)}`}
                            >
                                <CloseIcon style={{ fontSize: 12 }} />
                            </button>
                        </span>
                    ))}
                    <button
                        type='button'
                        onClick={onClearTools}
                        className='text-[12px] text-[#6b7280] underline hover:text-black'
                    >
                        Clear all
                    </button>
                </div>
            )}
        </div>
    )
}

export default TasksFilterBar
